import React from "react";
import { UserMessage } from "./UserMessage";
import { BotMessage } from "./components/BotMessage";
import { ChatMessage, ChatMessageArtifact } from "@store/messages.store";

interface BaseMessage {
  id: string;
  text?: string;
  customContent?: React.ReactNode;
  authorName: string;
  authorAvatar: string;
  type: string;
}

interface UserMessageProps extends BaseMessage {
  createdBy: "user";
  text: string;
}

export interface BotMessageProps extends BaseMessage {
  createdBy: "bot";
  subMessages?: Omit<BotMessageProps, "subMessages">[];
  artifacts?: ChatMessage["artifacts"];
  text?: string;
  customContent?: React.ReactNode;
  isPreparingMessageResponse?: boolean;
  defaultTimelineOpen?: boolean;
  onTimelineOpenChange?: (isOpen: boolean) => void;
  _metadata?: {
    elapsedTime: number;
  };
  onArtifactClick?: (artifact: ChatMessageArtifact) => void;
}

type MessageProps = UserMessageProps | BotMessageProps;

export const Message = React.forwardRef<HTMLDivElement, MessageProps>(
  (props, ref) => {
    const { createdBy } = props;

    if (createdBy === "user") {
      const { id, text, authorName, type } = props;
      return (
        <UserMessage
          id={id}
          text={text}
          authorName={authorName}
          type={type}
          ref={ref}
        />
      );
    }

    const {
      id,
      text,
      authorName,
      authorAvatar,
      customContent,
      type,
      subMessages,
      artifacts,
      isPreparingMessageResponse,
      defaultTimelineOpen,
      onTimelineOpenChange,
      _metadata,
      onArtifactClick,
    } = props;

    return (
      <BotMessage
        id={id}
        text={text}
        authorName={authorName}
        authorAvatar={authorAvatar}
        customContent={customContent}
        subMessages={subMessages}
        artifacts={artifacts}
        type={type}
        ref={ref}
        isPreparingMessageResponse={isPreparingMessageResponse}
        defaultTimelineOpen={defaultTimelineOpen}
        onTimelineOpenChange={onTimelineOpenChange}
        _metadata={_metadata}
        onArtifactClick={onArtifactClick}
      />
    );
  },
);

Message.displayName = "Message";
